<script setup lang="ts">
import {onMounted, ref, inject} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import * as yup from 'yup'
import {useField, useForm} from 'vee-validate';
import BrandedLogo from './components/BrandedLogo.vue';
import {PortalAuthKey} from './plugins/auth';
import AuthLayout from "./layouts/AuthLayout.vue";
import {useMessagesStore} from "./stores/messages.ts";

const route = useRoute()

const router = useRouter()

const messages = useMessagesStore()

// Inject plugin
const auth = inject(PortalAuthKey);
if (!auth) {
  throw new Error("PortalAuth plugin not found. Make sure it's installed in main.ts.");
}

// State for error snackbar
const loading = ref(false);
const checkedAuth = ref(false);

const schema = yup.object({
  username: yup.string().required().label('Username'),
  password: yup.string().required().label('Password')
})

const {handleSubmit} = useForm({
  validationSchema: schema,
})

const username = useField<string>('username')
const password = useField<string>('password')

// Submit handler
const onSubmit = handleSubmit(async (values: { username: string, password: string }) => {
  loading.value = true;
  try {
    await auth.login(values.username, values.password);
    checkedAuth.value = true;
  } catch (err) {
    messages.add({
      text: err?.message || "Invalid Credentials. Please try again!",
      color: "error",
    })
  } finally {
    loading.value = false;
  }
});

// QuickAuth Login (Magic Link, etc.)
onMounted(async () => {
  if (route.query.qa) {
    loading.value = true;
    try {
      await auth.quickAuthLogin(route.query.qa as string);
      checkedAuth.value = true;
    } catch (err) {
      messages.add({
        text: "Quick Auth Login Failed. Please try again!",
        color: "error",
      })
    } finally {
      loading.value = false;
    }
  }
});

</script>
<template>
  <AuthLayout>
    <v-sheet width="450" class="mx-auto pa-12 text-center" rounded="lg" elevation="8" v-if="!loading && !checkedAuth">
      <BrandedLogo></BrandedLogo>
      <br>
      <v-text-field autofocus label="Username" v-model="username.value.value"
                    :error-messages="username.errorMessage.value"></v-text-field>
      <v-text-field label="Password" type="password" v-model="password.value.value"
                    :error-messages="password.errorMessage.value"></v-text-field>
      <v-btn color="primary" block class="mt-2" size="large" @click="onSubmit">
        <span class="mx-2">Login</span>
      </v-btn>
      <div class="d-flex flex-column mt-2">
        <span class="text-caption text-primary cursor-pointer" @click="router.push({ name: 'app.forgotpass' })">Forgot
          Password?</span>
        <span class="text-caption text-primary cursor-pointer"
              @click="router.push({ name: 'app.resendwelcome' })">Resend
          Welcome Email</span>
      </div>
      <div class="mt-2">
        <p class="text-body-2 text-center">Copyright
          &copy; {{ new Date(Date.now()).getFullYear() }} OIX®</p>
        <p class="text-body-2 text-center mb-2">All Rights Reserved.</p>
        <v-divider></v-divider>
        <p class="text-body-2 text-center mt-2">
          By signing in, you agree to OIX's <a href="https://onlineimage.com/privacy-policy/" target="_blank"
                                                        class="text-primary">Privacy Policy</a>
          and <a href="https://onlineimage.com/terms-of-service/" target="_blank" class="text-primary">Terms of
          Service</a>.
        </p>
      </div>
    </v-sheet>
    <v-sheet width="450" class="mx-auto pa-12 text-center" rounded="lg" elevation="8" v-else-if="loading">
      <v-progress-circular :size="100" :width="8" color="primary" indeterminate></v-progress-circular>
    </v-sheet>
    <v-sheet width="450" class="mx-auto pa-12 text-center" rounded="lg" elevation="8"
             v-else-if="checkedAuth && !loading">
      <v-icon class="mb-5" color="success" icon="mdi-check-circle" size="112"></v-icon>
      <h2 class="text-h5 mb-6">You are now Logged In!</h2>
      <p class="mb-4 text-medium-emphasis text-body-2">You will be redirected momentarily.
      </p>
    </v-sheet>
  </AuthLayout>
</template>